import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, API } from "@/App";
import { 
  ArrowLeft, Edit, Trash2, Calendar, Utensils, Clock,
  Sun, Coffee, Moon, Cookie, CheckCircle, Save, X,
  ChevronLeft, ChevronRight, Apple, Loader2
} from "lucide-react";
import { toast } from "sonner";

const MEAL_CONFIG = {
  breakfast: { icon: Sun, label: "Breakfast", time: "7am - 9am", color: "text-yellow-500" },
  morning_snack: { icon: Apple, label: "Morning Snack", time: "10am - 11am", color: "text-green-500" },
  lunch: { icon: Coffee, label: "Lunch", time: "12pm - 2pm", color: "text-orange-500" },
  afternoon_snack: { icon: Cookie, label: "Afternoon Snack", time: "3pm - 4pm", color: "text-purple-500" },
  dinner: { icon: Moon, label: "Dinner", time: "6pm - 8pm", color: "text-blue-500" },
  evening_snack: { icon: Cookie, label: "Evening Snack", time: "9pm - 10pm", color: "text-indigo-500" },
  snacks: { icon: Cookie, label: "Snacks", time: "Anytime", color: "text-pink-500" }
};

// Helper to clean food text by removing redundant prefixes like "Week 1 Monday breakfast - "
const cleanFoodText = (text) => {
  if (!text) return text;
  // Pattern matches: "Week X DayName mealtype - " or "Week X DayName mealtype: "
  const pattern = /^(Week\s*\d+\s*)?(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday|Day\s*\d+)?\s*(breakfast|lunch|dinner|snack|morning snack|afternoon snack|evening snack)?\s*[-:]\s*/i;
  return text.replace(pattern, '').trim();
};

// Helper to clean day labels - replace "Page X" with "Week X"
const cleanDayLabel = (label) => {
  if (!label) return label;
  return label.replace(/^Page\s*/i, 'Week ');
};

const MyMealPlanDetail = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(0);
  const [selectedDayInWeek, setSelectedDayInWeek] = useState(0);
  const [loggedMeals, setLoggedMeals] = useState([]);
  const [loggingMeal, setLoggingMeal] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    days: []
  });

  // Calculate which day index based on week + day
  const selectedDay = selectedWeek * 7 + selectedDayInWeek;

  useEffect(() => {
    fetchPlan();
    fetchTodayLogs();
  }, [planId, token]);

  const fetchTodayLogs = async () => {
    try {
      // Try user plan logs first (for B2C users)
      const response = await fetch(`${API}/log-meal/user-plan/today`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      if (response.ok) {
        const data = await response.json();
        setLoggedMeals(data.logged_meals || []);
      }
    } catch (error) {
      console.error("Failed to fetch today's logs:", error);
    }
  };

  const handleQuickLog = async (mealType) => {
    setLoggingMeal(mealType);
    try {
      // Use user-plan endpoint for B2C user-created plans
      const response = await fetch(`${API}/log-meal/user-plan/quick/${mealType}`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        
      });
      
      if (response.ok) {
        setLoggedMeals(prev => [...prev, mealType]);
        toast.success(`${mealType.charAt(0).toUpperCase() + mealType.slice(1).replace('_', ' ')} logged!`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to log meal");
      }
    } catch (error) {
      console.error("Log meal error:", error);
      toast.error("Failed to log meal");
    } finally {
      setLoggingMeal(null);
    }
  };

  const isMealLogged = (mealType) => {
    return loggedMeals.includes(mealType);
  };

  const fetchPlan = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/meal-plans/${planId}`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        const data = await response.json();
        setPlan(data);
        
        // Initialize edit form - handle both old and new format
        const days = data.days || [{
          day_number: 1,
          day_label: "Day 1",
          meals: data.daily_breakdown || {}
        }];
        
        setEditForm({
          name: data.name || "",
          description: data.description || "",
          days: days
        });
      } else {
        toast.error("Meal plan not found");
        navigate("/my-meal-plans");
      }
    } catch (error) {
      console.error("Fetch plan error:", error);
      toast.error("Failed to load meal plan");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/meal-plans/${planId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        
        body: JSON.stringify({
          name: editForm.name,
          description: editForm.description,
          plan_duration: editForm.days.length,
          days: editForm.days,
          // Keep daily_breakdown for backwards compatibility
          daily_breakdown: editForm.days[0]?.meals || {},
          meals_per_day: Object.keys(editForm.days[0]?.meals || {}).filter(k => editForm.days[0]?.meals[k]?.foods).length
        }),
      });

      if (response.ok) {
        toast.success("Meal plan updated!");
        setIsEditing(false);
        fetchPlan();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to update meal plan");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to update meal plan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Are you sure you want to delete this meal plan?")) return;

    try {
      const response = await fetch(`${API}/meal-plans/${planId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
        
      });

      if (response.ok) {
        toast.success("Meal plan deleted");
        navigate("/my-meal-plans");
      } else {
        toast.error("Failed to delete meal plan");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete meal plan");
    }
  };

  const handleActivate = async () => {
    try {
      const response = await fetch(`${API}/meal-plans/${planId}/activate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        
      });

      if (response.ok) {
        toast.success("Meal plan activated!");
        fetchPlan();
      } else {
        toast.error("Failed to activate meal plan");
      }
    } catch (error) {
      console.error("Activate error:", error);
      toast.error("Failed to activate meal plan");
    }
  };

  const updateMeal = (dayIndex, mealKey, field, value) => {
    setEditForm(prev => {
      const newDays = [...prev.days];
      if (!newDays[dayIndex].meals) {
        newDays[dayIndex].meals = {};
      }
      if (!newDays[dayIndex].meals[mealKey]) {
        newDays[dayIndex].meals[mealKey] = { foods: "", notes: "" };
      }
      newDays[dayIndex].meals[mealKey][field] = value;
      return { ...prev, days: newDays };
    });
  };

  const addDay = () => {
    setEditForm(prev => ({
      ...prev,
      days: [
        ...prev.days,
        {
          day_number: prev.days.length + 1,
          day_label: `Day ${prev.days.length + 1}`,
          meals: {
            breakfast: { foods: "", notes: "" },
            lunch: { foods: "", notes: "" },
            dinner: { foods: "", notes: "" }
          }
        }
      ]
    }));
  };

  const removeDay = (dayIndex) => {
    if (editForm.days.length <= 1) {
      toast.error("Plan must have at least one day");
      return;
    }
    setEditForm(prev => ({
      ...prev,
      days: prev.days.filter((_, i) => i !== dayIndex).map((d, i) => ({
        ...d,
        day_number: i + 1,
        day_label: `Day ${i + 1}`
      }))
    }));
    if (selectedDay >= editForm.days.length - 1) {
      const prev = Math.max(0, selectedDay - 1);
      setSelectedWeek(Math.floor(prev / 7));
      setSelectedDayInWeek(prev % 7);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  // Get days array - handle both old and new format
  const getDays = () => {
    if (plan?.days && plan.days.length > 0) {
      return plan.days;
    }
    // Convert old format to new format
    if (plan?.daily_breakdown) {
      return [{
        day_number: 1,
        day_label: "Day 1",
        meals: plan.daily_breakdown
      }];
    }
    return [];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/20" />
          <p className="text-muted-foreground">Loading meal plan...</p>
        </div>
      </div>
    );
  }

  if (!plan) return null;

  const days = getDays();
  const currentDay = days[selectedDay] || days[0];

  return (
    <div className="min-h-screen bg-background pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b border-orange-100 bg-background/80 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/my-meal-plans" className="text-muted-foreground hover:text-secondary transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-heading text-xl font-bold text-secondary truncate">
              {isEditing ? "Edit Plan" : plan.name}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)} className="rounded-full">
                  <X className="w-4 h-4 mr-1" /> Cancel
                </Button>
                <Button size="sm" onClick={handleSaveEdit} disabled={saving} className="rounded-full bg-primary" data-testid="save-plan-btn">
                  <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save"}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} className="rounded-full" data-testid="edit-plan-btn">
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
                {!plan.is_active && (
                  <Button variant="outline" size="sm" onClick={handleActivate} className="rounded-full text-primary border-primary" data-testid="activate-plan-btn">
                    <CheckCircle className="w-4 h-4 mr-1" /> Set Active
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={handleDelete} className="text-red-500 hover:text-red-600 hover:bg-red-50" data-testid="delete-plan-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Plan Info */}
        <Card className="bg-white rounded-3xl border border-orange-100/50">
          <CardContent className="p-5">
            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-name">Plan Name</Label>
                  <Input id="edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea id="edit-description" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="mt-1" rows={2} />
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="font-heading text-2xl font-bold text-secondary">{plan.name}</h2>
                  {plan.is_active && <Badge className="bg-green-100 text-green-700 border-0">Active</Badge>}
                  {plan.source === "upload" && <Badge variant="outline">Uploaded</Badge>}
                  <Badge variant="secondary">{days.length} {days.length === 1 ? "Day" : "Days"}</Badge>
                </div>
                {plan.description && <p className="mt-2 text-muted-foreground">{plan.description}</p>}
                <div className="mt-3 flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> Created {formatDate(plan.created_at)}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Week & Day Navigation */}
        {days.length > 1 && (() => {
          const totalWeeks = Math.ceil(days.length / 7);
          const daysInCurrentWeek = isEditing ? editForm.days : days;
          const startIndex = selectedWeek * 7;
          const endIndex = Math.min(startIndex + 7, daysInCurrentWeek.length);
          const currentWeekDays = daysInCurrentWeek.slice(startIndex, endIndex);
          
          return (
            <div className="space-y-2">
              {/* Week Selector - only show if more than 7 days */}
              {days.length > 7 && (
                <div className="flex items-center justify-center gap-2 bg-white rounded-2xl p-2 border border-orange-100/50">
                  <span className="text-sm text-muted-foreground mr-2">Week:</span>
                  {Array.from({ length: totalWeeks }, (_, i) => (
                    <Button
                      key={i}
                      variant={selectedWeek === i ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        setSelectedWeek(i);
                        setSelectedDayInWeek(0);
                      }}
                      className={`rounded-full min-w-[40px] ${selectedWeek === i ? "bg-primary" : ""}`}
                    >
                      W{i + 1}
                    </Button>
                  ))}
                </div>
              )}
              
              {/* Day Selector - Scrollable */}
              <div className="bg-white rounded-2xl p-2 border border-orange-100/50">
                <div className="flex items-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (selectedDayInWeek > 0) {
                        setSelectedDayInWeek(selectedDayInWeek - 1);
                      } else if (selectedWeek > 0) {
                        setSelectedWeek(selectedWeek - 1);
                        setSelectedDayInWeek(6);
                      }
                    }}
                    disabled={selectedWeek === 0 && selectedDayInWeek === 0}
                    className="rounded-full flex-shrink-0 p-1 h-8 w-8"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  
                  <div 
                    className="flex gap-1 px-1 flex-1 overflow-x-auto touch-pan-x"
                    style={{ 
                      scrollbarWidth: 'none', 
                      msOverflowStyle: 'none',
                      WebkitOverflowScrolling: 'touch'
                    }}
                  >
                    <style>{`.touch-pan-x::-webkit-scrollbar { display: none; }`}</style>
                    {currentWeekDays.map((day, index) => {
                      const globalIndex = startIndex + index;
                      const dayLabels = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
                      let abbrevLabel = dayLabels[index] || `D${globalIndex + 1}`;
                      
                      const dayLabel = day.day_label || "";
                      if (/monday/i.test(dayLabel)) abbrevLabel = "MON";
                      else if (/tuesday/i.test(dayLabel)) abbrevLabel = "TUE";
                      else if (/wednesday/i.test(dayLabel)) abbrevLabel = "WED";
                      else if (/thursday/i.test(dayLabel)) abbrevLabel = "THU";
                      else if (/friday/i.test(dayLabel)) abbrevLabel = "FRI";
                      else if (/saturday/i.test(dayLabel)) abbrevLabel = "SAT";
                      else if (/sunday/i.test(dayLabel)) abbrevLabel = "SUN";
                      else if (/day\s*\d+/i.test(dayLabel)) abbrevLabel = `D${globalIndex + 1}`;
                      
                      return (
                        <Button
                          key={globalIndex}
                          variant={selectedDayInWeek === index ? "default" : "ghost"}
                          size="sm"
                          onClick={() => setSelectedDayInWeek(index)}
                          className={`rounded-full min-w-[42px] h-8 px-2 text-xs flex-shrink-0 ${selectedDayInWeek === index ? "bg-primary" : ""}`}
                        >
                          {abbrevLabel}
                        </Button>
                      );
                    })}
                    {isEditing && (
                      <Button variant="outline" size="sm" onClick={addDay} className="rounded-full h-8 px-2 text-xs flex-shrink-0">
                        +
                      </Button>
                    )}
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (selectedDayInWeek < currentWeekDays.length - 1) {
                        setSelectedDayInWeek(selectedDayInWeek + 1);
                      } else if (selectedWeek < totalWeeks - 1) {
                        setSelectedWeek(selectedWeek + 1);
                        setSelectedDayInWeek(0);
                      }
                    }}
                    disabled={selectedWeek === totalWeeks - 1 && selectedDayInWeek === currentWeekDays.length - 1}
                    className="rounded-full flex-shrink-0 p-1 h-8 w-8"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Single day - show add day button in edit mode */}
        {days.length === 1 && isEditing && (
          <div className="flex justify-center">
            <Button variant="outline" onClick={addDay} className="rounded-full">
              + Add Another Day
            </Button>
          </div>
        )}

        {/* Day Header with Delete (Edit Mode) */}
        {isEditing && editForm.days.length > 1 && (
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-lg font-semibold text-secondary">
              {cleanDayLabel(editForm.days[selectedDay]?.day_label || `Day ${selectedDay + 1}`)}
            </h3>
            <Button variant="ghost" size="sm" onClick={() => removeDay(selectedDay)} className="text-red-500 hover:bg-red-50">
              <Trash2 className="w-4 h-4 mr-1" /> Remove Day
            </Button>
          </div>
        )}

        {/* Meals for Selected Day */}
        <div className="space-y-4">
          <h3 className="font-heading text-lg font-semibold text-secondary px-1">
            {!isEditing && cleanDayLabel(currentDay?.day_label || `Day ${selectedDay + 1}`)} - Meals
          </h3>
          
          {isEditing ? (
            // Edit Mode - show all meal slots
            Object.entries(MEAL_CONFIG).map(([mealKey, config]) => {
              const Icon = config.icon;
              const mealData = editForm.days[selectedDay]?.meals?.[mealKey] || { foods: "", notes: "" };
              
              return (
                <Card key={mealKey} className="bg-white rounded-2xl border border-orange-100/50" data-testid={`edit-meal-${mealKey}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-6 h-6 ${config.color}`} />
                      </div>
                      <div className="flex-1 min-w-0 space-y-3">
                        <h4 className="font-semibold text-secondary">{config.label}</h4>
                        <div>
                          <Label className="text-xs text-muted-foreground">Foods</Label>
                          <Textarea
                            value={mealData.foods || ""}
                            onChange={(e) => updateMeal(selectedDay, mealKey, "foods", e.target.value)}
                            placeholder={`Enter ${config.label.toLowerCase()} foods with portions`}
                            className="mt-1"
                            rows={2}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Notes (optional)</Label>
                          <Input
                            value={mealData.notes || ""}
                            onChange={(e) => updateMeal(selectedDay, mealKey, "notes", e.target.value)}
                            placeholder="Any special instructions"
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            // View Mode - show only meals with content
            Object.entries(currentDay?.meals || {}).map(([mealKey, mealData]) => {
              if (!mealData?.foods) return null;
              
              const config = MEAL_CONFIG[mealKey] || { icon: Utensils, label: mealKey, time: "", color: "text-gray-500" };
              const Icon = config.icon;
              const cleanedFoods = cleanFoodText(mealData.foods);
              const logged = isMealLogged(mealKey);
              const isLogging = loggingMeal === mealKey;
              
              return (
                <Card key={mealKey} className={`bg-white rounded-2xl border ${logged ? 'border-green-200 bg-green-50/30' : 'border-orange-100/50'}`} data-testid={`meal-${mealKey}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl ${logged ? 'bg-green-100' : 'bg-primary/10'} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-6 h-6 ${logged ? 'text-green-500' : config.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-secondary">{config.label}</h4>
                            {logged && <CheckCircle className="w-4 h-4 text-green-500" />}
                          </div>
                          <div className="flex items-center gap-2">
                            {config.time && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {config.time}
                              </span>
                            )}
                            {/* Log Button */}
                            {!logged && plan?.is_active && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="ml-2 text-green-600 border-green-200 hover:bg-green-50"
                                onClick={() => handleQuickLog(mealKey)}
                                disabled={isLogging}
                                data-testid={`log-${mealKey}-btn`}
                              >
                                {isLogging ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  "Log"
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                        <p className="mt-2 text-muted-foreground whitespace-pre-wrap">{cleanedFoods}</p>
                        {mealData.notes && (
                          <p className="mt-2 text-sm text-primary bg-primary/5 p-2 rounded-lg">
                            {mealData.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Tips */}
        {!isEditing && (
          <Card className="bg-gradient-to-r from-orange-50 to-primary/5 rounded-3xl border border-orange-100/50">
            <CardContent className="p-5">
              <h4 className="font-semibold text-secondary mb-2">Tips for Following This Plan</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Stick to the portion sizes mentioned</li>
                <li>• Drink plenty of water throughout the day</li>
                <li>• Log your meals to track compliance</li>
                <li>• Adjust timing based on your schedule</li>
              </ul>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default MyMealPlanDetail;
